import React, { useRef, useState } from "react";
import AppShell from "../components/AppShell";
import { API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Upload, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";

function UploadCard({ title, description, endpoint, testid, sampleHeader }) {
  const ref = useRef();
  const [loading, setLoading] = useState(false);
  const onSelect = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await fetch(`${API}${endpoint}`, { method: "POST", body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Upload failed");
      toast.success(`Imported ${data.created} records`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };
  return (
    <div className="bg-white border border-border rounded-md p-6 shadow-sm" data-testid={testid}>
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-md bg-blue-50 flex items-center justify-center text-[#0A4C95]"><FileSpreadsheet className="w-5 h-5" /></div>
        <div className="flex-1">
          <h3 className="font-semibold">{title}</h3>
          <p className="text-sm text-slate-500 mt-1">{description}</p>
          <div className="mt-3 text-[11px] text-slate-500 bg-slate-50 p-2 rounded border border-slate-200 font-mono-fin">CSV headers: {sampleHeader}</div>
          <div className="mt-4 flex items-center gap-2">
            <input ref={ref} type="file" accept=".csv" hidden onChange={onSelect} />
            <Button disabled={loading} className="bg-[#0A4C95] hover:bg-[#083d79]" onClick={() => ref.current?.click()}>
              <Upload className="w-4 h-4 mr-1.5" /> {loading ? "Uploading..." : "Upload CSV"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Uploads() {
  return (
    <AppShell
      title="Data Uploads"
      subtitle="Import revenue from eClinPro and monthly timesheets"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UploadCard
          testid="upload-revenues"
          title="eClinPro Revenue Report"
          description="Upload monthly grant payments, milestones, and startup fees received per site/study."
          endpoint="/revenues/upload"
          sampleHeader="site_id, cta_id, entry_date, amount, revenue_type, status, invoice_date, collected_date, due_date, visit_completion_date"
        />
        <UploadCard
          testid="upload-timesheets"
          title="Monthly Timesheet Report"
          description="Bulk import staff hours allocated by site and study."
          endpoint="/timesheets/upload"
          sampleHeader="employee_id, period_month, period_year, site_id, cta_id, hours"
        />
      </div>

      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-md p-5 text-sm text-blue-900 flex items-start gap-3">
        <FileText className="w-5 h-5 mt-0.5" />
        <div>
          <div className="font-semibold">Tip</div>
          <div className="mt-1 text-blue-900/90">Download your eClinPro Revenue Export as CSV and map columns to the headers shown above. Leave optional fields blank.</div>
        </div>
      </div>
    </AppShell>
  );
}
